import { cn } from '@/lib/utils';

interface FoundingMemberBadgeProps {
  /** 'sm' = inline chip, 'md' = header, 'lg' = settings card */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function FoundingMemberBadge({ size = 'md', className }: FoundingMemberBadgeProps) {
  if (size === 'sm') {
    return (
      <span
        title="Founding Member"
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full px-1 py-px text-[9px] font-bold tracking-wide leading-none',
          'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500',
          'text-amber-900 border border-amber-300/60',
          className,
        )}
      >
        <svg className="h-2 w-2 shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
        </svg>
        FM
      </span>
    );
  }

  if (size === 'lg') {
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border border-amber-300/50 p-3',
          'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100',
          'dark:from-amber-950/40 dark:via-yellow-950/30 dark:to-amber-900/40 dark:border-amber-600/40',
          'shadow-[0_2px_12px_-2px_rgba(217,119,6,0.25)]',
          className,
        )}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 pointer-events-none" />
        <div className="relative flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 shadow-md shrink-0">
            <svg className="h-4 w-4 text-amber-900" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 leading-none">
              Founding Member
            </p>
            <p className="text-xs text-amber-800/70 dark:text-amber-300/60 mt-0.5 leading-tight">
              Lifetime access · Launch cohort
            </p>
          </div>
        </div>
      </div>
    );
  }

  // md — header badge
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold tracking-wide',
        'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500',
        'text-amber-900 shadow-md border border-amber-300/70',
        'dark:from-amber-500 dark:via-yellow-400 dark:to-amber-500 dark:border-amber-400/50',
        className,
      )}
    >
      <svg className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
      </svg>
      Founding Member
    </span>
  );
}
